import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useMemo, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as yup from "yup";
import { errorHandler } from "_services/errorHandler";
import {
  useTransportResourceServiceV1GetVendorDetailQuery,
  useTransportResourceServiceV1UpdateVendorMutation,
  useTransportResourceServiceV1UpdateVendorDraftMutation,
  useTransportResourceServiceV1PublishVendorDraftMutation,
  useTransportResourceServiceV1GetVendorDraftQuery,
  TransportResourceCreateVendorReq,
  TransportResourceCreateVendorDraftReq,
  TransportResourceServiceV1UpdateVendorBody,
  TransportResourceServiceV1UpdateVendorDraftBody,
  TransportResourceGetVendorDetailRes,
  TransportResourceCreateVendorBranchItem,
  useTransportResourceServiceV1UploadVendorLogoMutation,
  TransportResourceServiceV1UploadVendorLogoBody,
  vendorApi,
} from "_services/modules/transport-resource/vendor";

import {
  VendorAddressFormValues,
  VendorAddressItem,
  VendorBranchFormValues,
  VendorBranchItem,
  VendorInformationFormValues,
  VendorAddressCategory,
  ExtendedVendorDetail,
  ExtendedUpdateVendorBody,
} from "_interfaces/vendor.interfaces";
import { useAppTranslation } from "locale/useAppTranslation";
import { vendorPageRouteName } from "modules/master-data/vendor/constants/routes";
import { useAppDispatch } from "store";
import { setGlobalComponent } from "store/global-components";
import { isHeadquarterCategory, getAvailableBranchOptions } from "data/address-category";
import { useLocation } from "react-router-dom";

export type { VendorAddressItem, VendorBranchItem } from "_interfaces/vendor.interfaces";

const vendorInfoSchema = yup
  .object({
    vendorType: yup
      .mixed<"individual" | "company">()
      .oneOf(["individual", "company"])
      .required(),
    vendorName: yup.string().required("Vendor name is required"),
    vendorId: yup.string().notRequired(),
    npwp: yup.string().required("NPWP is required"),
    email: yup
      .string()
      .email("Please enter a valid email")
      .required("Vendor email is required"),
    contactPersonName: yup.string().required("Contact person name is required"),
    contactPersonPhoneCountryCode: yup.string().required(),
    contactPersonPhoneNumber: yup
      .string()
      .required("Contact person phone number is required"),
    legalName: yup.string().when("vendorType", {
      is: "company",
      then: (schema) => schema.required("Legal name is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    status: yup.mixed<"active" | "inactive">().oneOf(["active", "inactive"]).required(),
  })
  .required();

const vendorBranchSchema = yup
  .object({
    branchName: yup.string().required("Branch name is required"),
    picName: yup.string().required("PIC name is required"),
    picEmail: yup
      .string()
      .email("Please enter a valid email")
      .required("PIC email is required"),
    picCountryCode: yup.string().required(),
    picPhoneNumber: yup.string().required("PIC phone number is required"),
  })
  .required();

const vendorAddressSchema = yup
  .object({
    addressName: yup.string().required("Address name is required"),
    addressCategory: yup.string().required("Address category is required"),
    branch: yup.string().required("Branch is required"),
    country: yup.string().required("Country is required"),
    province: yup.string().required("Province is required"),
    city: yup.string().required("City is required"),
    postalCode: yup.string().required("Postal code is required"),
    fullAddress: yup.string().required("Full address is required"),
    mapsUrl: yup.string().required("Google Maps URL is required"),
    isBillingAddress: yup.boolean().default(false),
  })
  .required();

const initialBranchItems: VendorBranchItem[] = [];

const initialAddressItems: VendorAddressItem[] = [];

const useEditVendorDraft = (id: string) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useAppTranslation("vendor");

  const isDraft = location.state?.from === "draft";

  const { data: vendorDataNormal, isLoading: isFetchingVendorNormal } = useTransportResourceServiceV1GetVendorDetailQuery(
    { id },
    { skip: !id || isDraft }
  );

  const { data: vendorDataDraft, isLoading: isFetchingVendorDraft } = useTransportResourceServiceV1GetVendorDraftQuery(
    { id },
    { skip: !id || !isDraft }
  );

  const vendorData = isDraft ? vendorDataDraft : vendorDataNormal;
  const isFetchingVendor = isDraft ? isFetchingVendorDraft : isFetchingVendorNormal;

  const [updateVendor, { isLoading: isUpdatingVendor }] =
    useTransportResourceServiceV1UpdateVendorMutation();
  const [updateVendorDraft, { isLoading: isUpdatingVendorDraft }] =
    useTransportResourceServiceV1UpdateVendorDraftMutation();
  const [publishVendorDraft, { isLoading: isPublishingVendorDraft }] =
    useTransportResourceServiceV1PublishVendorDraftMutation();
  const [uploadLogo] = useTransportResourceServiceV1UploadVendorLogoMutation();

  const vendorInfoForm = useForm<VendorInformationFormValues>({
    mode: "onChange",
    resolver: yupResolver(vendorInfoSchema),
    defaultValues: {
      vendorType: "company",
      vendorName: "",
      vendorId: "",
      npwp: "",
      email: "",
      contactPersonName: "",
      contactPersonPhoneCountryCode: "+62",
      contactPersonPhoneNumber: "",
      legalName: "",
      status: "active",
    },
  });

  const vendorBranchForm = useForm<VendorBranchFormValues>({
    mode: "onChange",
    resolver: yupResolver(vendorBranchSchema),
    defaultValues: {
      branchName: "",
      picName: "",
      picEmail: "",
      picCountryCode: "+62",
      picPhoneNumber: "",
    },
  });

  const vendorAddressForm = useForm<VendorAddressFormValues>({
    mode: "onChange",
    resolver: yupResolver(vendorAddressSchema),
    defaultValues: {
      addressName: "",
      addressCategory: "branch",
      branch: "",
      country: "",
      province: "",
      city: "",
      postalCode: "",
      fullAddress: "",
      mapsUrl: "",
      isBillingAddress: false,
    },
  });

  const [openSections, setOpenSections] = useState({
    vendorInformation: true,
    vendorBranch: false,
    vendorAddress: false,
  });
  const [branchItems, setBranchItems] = useState<VendorBranchItem[]>(initialBranchItems);
  const [addressItems, setAddressItems] = useState<VendorAddressItem[]>(initialAddressItems);
  const [editingBranchIndex, setEditingBranchIndex] = useState<number | null>(null);
  const [editingAddressIndex, setEditingAddressIndex] = useState<number | null>(null);

  const [showCancelModal, setShowCancelModal] = useState(false);

  useEffect(() => {
    dispatch(
      setGlobalComponent({
        title: t("page-title-edit") || "Edit Vendor",
        hasBackButton: true,
        customBackEvent: "vendor-edit-back",
      }),
    );
    
    return () => {
      dispatch(
        setGlobalComponent({
          title: "Vendor",
          hasBackButton: false,
          customBackEvent: undefined,
        }),
      );
    };
  }, [dispatch, t]);

  useEffect(() => {
    const handleBack = () => {
      if (
        vendorInfoForm.formState.isDirty ||
        vendorBranchForm.formState.isDirty ||
        vendorAddressForm.formState.isDirty
      ) {
        setShowCancelModal(true);
      } else {
        if (isDraft) {
          navigate("/master-data/vendor/draft");
        } else {
          navigate(vendorPageRouteName);
        }
      }
    };

    window.addEventListener("vendor-edit-back", handleBack);
    return () => window.removeEventListener("vendor-edit-back", handleBack);
  }, [
    vendorInfoForm.formState.isDirty,
    vendorBranchForm.formState.isDirty,
    vendorAddressForm.formState.isDirty,
    navigate,
    isDraft
  ]);

  const hasHydrated = useRef(false);

  const parsePicPhone = (phone = "") => {
    if (phone.startsWith("+62")) {
      return { countryCode: "+62", number: phone.slice(3) };
    }
    if (phone.startsWith("+")) {
      return { countryCode: phone.slice(0, 3), number: phone.slice(3) };
    }
    return { countryCode: "+62", number: phone };
  };

  const cleanPhone = (countryCode: string, phoneNumber: string) => {
    const code = countryCode.replace(/\s+/g, "");
    let num = phoneNumber.replace(/\D/g, "");
    if (num.startsWith("0")) {
      num = num.slice(1);
    }
    return `${code}${num}`;
  };

  useEffect(() => {
    if (vendorData && !hasHydrated.current) {
      const v = vendorData as ExtendedVendorDetail;
      const parsedContactPhone = parsePicPhone(v.contact_person_phone_number || "+62");
      vendorInfoForm.reset({
        vendorType: (v.vendor_type === "individual" || v.vendor_type === "company") ? v.vendor_type : "company",
        vendorName: v.vendor_name || v.name || "",
        vendorId: v.vendor_code || v.code || "",
        npwp: v.npwp || "",
        email: v.email || v.vendor_email || "",
        contactPersonName: v.contact_person_name || "",
        contactPersonPhoneCountryCode: parsedContactPhone.countryCode,
        contactPersonPhoneNumber: parsedContactPhone.number,
        legalName: v.legal_name || "",
        status: v.status?.toLowerCase() === "inactive" ? "inactive" : "active",
        vendorLogo: v.vendor_photo_url || v.vendor_logo || "",
      });

      if (v.branches && Array.isArray(v.branches)) {
        const newBranchItems: VendorBranchItem[] = [];
        const newAddressItems: VendorAddressItem[] = [];

        v.branches.forEach((b: any) => {
          const { countryCode: picCountryCode, number: picPhoneNumber } = parsePicPhone(b.pic_phone || "");

          if (!newBranchItems.find(x => x.branchName === b.branch_name)) {
            newBranchItems.push({
              branchName: b.branch_name || "",
              picName: b.pic_name || "",
              picEmail: b.pic_email || "",
              picCountryCode,
              picPhoneNumber,
            });
          }
          
          if (b.addresses && Array.isArray(b.addresses)) {
             b.addresses.forEach((a: any) => {
                newAddressItems.push({
                  addressName: a.address_name || "",
                  addressCategory: a.address_category || "",
                  branch: b.branch_name || "",
                  fullAddress: a.full_address || "",
                  country: a.country_id || a.country || "Indonesia",
                  province: a.province_id || a.province || "",
                  city: a.city_id || a.city || "",
                  postalCode: a.postal_code || "",
                  mapsUrl: a.google_maps_url || "",
                  isBillingAddress: !!a.is_billing_address,
                  countryName: a.country_name || a.country || "Indonesia",
                  provinceName: a.province_name || a.province || a.state_name,
                  cityName: a.city_name || a.city,
                });
             });
          } else if (b.address_name) {
            newAddressItems.push({
              addressName: b.address_name,
              addressCategory: b.address_category || "",
              branch: b.branch_name || "",
              fullAddress: b.full_address || "",
              country: b.country_id || b.country || "Indonesia",
              province: b.province_id || b.province || "",
              city: b.city_id || b.city || "",
              postalCode: b.postal_code || "",
              mapsUrl: b.google_maps_url || "",
              isBillingAddress: !!b.is_billing_address,
              countryName: b.country_name || b.country || "Indonesia",
              provinceName: b.province_name || b.province || b.state_name,
              cityName: b.city_name || b.city,
            });
          }
        });

        setBranchItems(newBranchItems);
        setAddressItems(newAddressItems);
      }
      hasHydrated.current = true;
    }
  }, [vendorData, vendorInfoForm]);

  const branchOptions = useMemo(
    () => getAvailableBranchOptions(branchItems, addressItems, editingAddressIndex),
    [branchItems, addressItems, editingAddressIndex],
  );

  const hasBillingAddress = useMemo(
    () => addressItems.some((item) => item.isBillingAddress),
    [addressItems],
  );

  const toggleSection = (section: keyof typeof openSections) => {
    setOpenSections((current) => ({
      ...current,
      [section]: !current[section],
    }));
  };

  const openSection = (section: keyof typeof openSections) => {
    setOpenSections((current) => ({
      ...current,
      [section]: true,
    }));
  };

  const handleNextFromInformation = async () => {
    setOpenSections((current) => ({
      ...current,
      vendorInformation: false,
      vendorBranch: true,
    }));
  };

  const handleNextFromBranch = async () => {
    setOpenSections((current) => ({
      ...current,
      vendorBranch: false,
      vendorAddress: true,
    }));
  };

  const handleAddBranch = async () => {
    const isValid = await vendorBranchForm.trigger();
    if (!isValid) {
      return;
    }

    const values = vendorBranchForm.getValues();
    const nextBranch: VendorBranchItem = {
      branchName: values.branchName,
      picName: values.picName,
      picEmail: values.picEmail,
      picCountryCode: values.picCountryCode,
      picPhoneNumber: values.picPhoneNumber,
    };

    if (editingBranchIndex !== null) {
      setBranchItems((current) =>
        current.map((item, index) =>
          index === editingBranchIndex ? nextBranch : item,
        ),
      );
      setEditingBranchIndex(null);
    } else {
      setBranchItems((current) => [...current, nextBranch]);
    }

    vendorBranchForm.reset({
      branchName: "",
      picName: "",
      picEmail: "",
      picCountryCode: "+62",
      picPhoneNumber: "",
    });
  };

  const handleEditBranch = (index: number) => {
    const item = branchItems[index];
    setEditingBranchIndex(index);
    vendorBranchForm.reset({
      branchName: item.branchName,
      picName: item.picName,
      picEmail: item.picEmail,
      picCountryCode: item.picCountryCode,
      picPhoneNumber: item.picPhoneNumber,
    });
  };

  const handleDeleteBranch = (index: number) => {
    if (branchItems.length <= 1) {
      toast.error(t("toast-branch-required") || "Vendor must have at least one branch");
      return;
    }
    const deletedBranchName = branchItems[index].branchName;
    setBranchItems((current) => current.filter((_, itemIndex) => itemIndex !== index));
    setAddressItems((current) => current.filter((item) => item.branch !== deletedBranchName));
    if (editingBranchIndex === index) {
      setEditingBranchIndex(null);
    }
  };

  const handleAddAddress = async () => {
    const isValid = await vendorAddressForm.trigger();
    if (!isValid) {
      return;
    }

    const values = vendorAddressForm.getValues();
    const nextAddress: VendorAddressItem = {
      addressName: values.addressName,
      addressCategory: values.addressCategory as VendorAddressCategory,
      branch: values.branch,
      fullAddress: values.fullAddress,
      country: values.country,
      province: values.province,
      city: values.city,
      postalCode: values.postalCode,
      mapsUrl: values.mapsUrl,
      isBillingAddress: values.isBillingAddress,
      countryName: values.countryName || (values.country === "102" ? "Indonesia" : values.country),
      provinceName: values.provinceName || values.province,
      cityName: values.cityName || values.city,
    };

    const isBranchAlreadyUsed = addressItems.some(
      (item, index) => index !== editingAddressIndex && item.branch === values.branch,
    );
    if (isBranchAlreadyUsed) {
      toast.error("Branch already has an address assigned");
      return;
    }

    if (isHeadquarterCategory(values.addressCategory)) {
      const isHqAlreadyUsed = addressItems.some(
        (item, index) => index !== editingAddressIndex && isHeadquarterCategory(item.addressCategory),
      );
      if (isHqAlreadyUsed) {
        toast.error("Only one Headquarter address is allowed");
        return;
      }
    }

    if (editingAddressIndex !== null) {
      setAddressItems((current) =>
        current.map((item, index) =>
          index === editingAddressIndex ? nextAddress : item,
        ),
      );
      setEditingAddressIndex(null);
    } else {
      setAddressItems((current) => [...current, nextAddress]);
    }

    vendorAddressForm.reset({
      addressName: "",
      addressCategory: "branch",
      branch: "",
      country: "",
      province: "",
      city: "",
      postalCode: "",
      fullAddress: "",
      mapsUrl: "",
      isBillingAddress: false,
      countryName: "",
      provinceName: "",
      cityName: "",
    });
  };

  const handleEditAddress = (index: number) => {
    const item = addressItems[index];
    setEditingAddressIndex(index);
    vendorAddressForm.reset({
      addressName: item.addressName,
      addressCategory: item.addressCategory,
      branch: item.branch,
      country: item.country,
      province: item.province,
      city: item.city,
      postalCode: item.postalCode,
      fullAddress: item.fullAddress,
      mapsUrl: item.mapsUrl,
      isBillingAddress: item.isBillingAddress,
      countryName: item.countryName,
      provinceName: item.provinceName,
      cityName: item.cityName,
    });
  };

  const handleDeleteAddress = (index: number) => {
    const targetAddress = addressItems[index];
    if (targetAddress) {
      const branchAddressesCount = addressItems.filter(
        (item) => item.branch === targetAddress.branch,
      ).length;
      if (branchAddressesCount <= 1) {
        toast.error(
          t("toast-all-branches-must-have-address") ||
            "Each branch must have at least one address",
        );
        return;
      }
    }
    setAddressItems((current) => current.filter((_, itemIndex) => itemIndex !== index));
    if (editingAddressIndex === index) {
      setEditingAddressIndex(null);
    }
  };

  const handleConfirmAddress = () => {
    if (addressItems.length === 0) {
      toast.error(t("toast-address-required"));
      return;
    }
    if (!hasBillingAddress) {
      toast.error(t("toast-billing-required"));
      return;
    }
    toast.success(t("toast-address-confirmed"));
  };

  const handleSaveDraft = async () => {
    const vendorInfo = vendorInfoForm.getValues();
    if (!vendorInfo.vendorName || !vendorInfo.vendorName.trim()) {
      openSection("vendorInformation");
      vendorInfoForm.setError("vendorName", {
        type: "manual",
        message: "Vendor name is required",
      });
      toast.error("Vendor Name is required to save draft");
      return;
    }

    const branchesPayload = branchItems.map((branchItem) => {
      const relatedAddresses = addressItems.filter(
        (address) => address.branch === branchItem.branchName,
      );

      return {
        branch_name: branchItem.branchName,
        pic_name: branchItem.picName,
        pic_email: branchItem.picEmail,
        pic_phone: cleanPhone(branchItem.picCountryCode, branchItem.picPhoneNumber),

        addresses: relatedAddresses.map((addressItem) => ({
          address_name: addressItem.addressName,
          address_category: addressItem.addressCategory,
          full_address: addressItem.fullAddress,
          province_id: addressItem.province,
          city_id: addressItem.city,
          postal_code: addressItem.postalCode,
          google_maps_url: addressItem.mapsUrl,
          is_billing_address: addressItem.isBillingAddress,
        })),
      };
    });

    const draftPayload: TransportResourceCreateVendorDraftReq = {
      ...(vendorInfo.vendorName ? { vendor_name: vendorInfo.vendorName } : {}),
      ...(vendorInfo.legalName ? { legal_name: vendorInfo.legalName } : {}),
      ...(vendorInfo.npwp ? { npwp: vendorInfo.npwp } : {}),
      ...(vendorInfo.vendorType ? { vendor_type: vendorInfo.vendorType } : {}),
      ...(vendorInfo.vendorId?.trim() ? { vendor_code: vendorInfo.vendorId.trim() } : {}),
      ...(vendorInfo.email ? { email: vendorInfo.email } : {}),
      ...(vendorInfo.contactPersonName ? { contact_person_name: vendorInfo.contactPersonName } : {}),
      ...(vendorInfo.contactPersonPhoneNumber
        ? {
            contact_person_phone_number: cleanPhone(
              vendorInfo.contactPersonPhoneCountryCode,
              vendorInfo.contactPersonPhoneNumber,
            ),
          }
        : {}),
      ...(branchesPayload.length > 0 ? { branches: branchesPayload } : {}),
    };

    try {
      await updateVendorDraft({
        id,
        transportResourceServiceV1UpdateVendorDraftBody: draftPayload,
      }).unwrap();

      const vendorLogoFile = vendorInfoForm.getValues("vendorLogoFile");
      if (vendorLogoFile && id) {
        const formData = new FormData();
        formData.append("file", vendorLogoFile);
        await uploadLogo({
          id,
          transportResourceServiceV1UploadVendorLogoBody: formData as TransportResourceServiceV1UploadVendorLogoBody,
        }).unwrap();
      }

      dispatch(vendorApi.util.invalidateTags([{ type: "Vendor", id: "DRAFT_LIST" }, { type: "Vendor", id: "LIST" }]));

      toast.success(t("toast-draft-saved") || "Draft saved");
      if (isDraft) {
        navigate("/master-data/vendor/draft");
      } else {
        navigate(vendorPageRouteName);
      }
    } catch (error) {
      errorHandler(error);
    }
  };

  const handleSave = async () => {
    const isValidVendorInfo = await vendorInfoForm.trigger();
    if (!isValidVendorInfo) {
      openSection("vendorInformation");
      toast.error(t("toast-complete-required-fields"));
      return;
    }

    if (branchItems.length === 0) {
      openSection("vendorBranch");
      toast.error(t("toast-branch-required"));
      return;
    }

    if (addressItems.length === 0) {
      openSection("vendorAddress");
      toast.error(t("toast-address-required"));
      return;
    }

    const hasAddressForEveryBranch = branchItems.every((branch) =>
      addressItems.some((address) => address.branch === branch.branchName),
    );

    if (!hasAddressForEveryBranch) {
      openSection("vendorAddress");
      toast.error(t("toast-all-branches-must-have-address"));
      return;
    }

    if (!hasBillingAddress) {
      openSection("vendorAddress");
      toast.error(t("toast-billing-required"));
      return;
    }

    const vendorInfo = vendorInfoForm.getValues();
    const branchesPayload = branchItems.map((branchItem) => {
      const relatedAddresses = addressItems.filter(
        (address) => address.branch === branchItem.branchName,
      );

      return {
        branch_name: branchItem.branchName,
        pic_name: branchItem.picName,
        pic_email: branchItem.picEmail,
        pic_phone: cleanPhone(branchItem.picCountryCode, branchItem.picPhoneNumber),

        addresses: relatedAddresses.map((addressItem) => ({
          address_name: addressItem.addressName,
          address_category: addressItem.addressCategory,
          full_address: addressItem.fullAddress,
          province_id: addressItem.province,
          city_id: addressItem.city,
          postal_code: addressItem.postalCode,
          google_maps_url: addressItem.mapsUrl,
          is_billing_address: addressItem.isBillingAddress,
        })),
      };
    });

    try {
      if (isDraft) {
        const updateDraftPayload: TransportResourceServiceV1UpdateVendorDraftBody = {
          vendor_name: vendorInfo.vendorName,
          legal_name:
            vendorInfo.vendorType === "company" && vendorInfo.legalName
              ? vendorInfo.legalName
              : undefined,
          npwp: vendorInfo.npwp,
          vendor_type: vendorInfo.vendorType,
          email: vendorInfo.email,
          contact_person_name: vendorInfo.contactPersonName,
          contact_person_phone_number: cleanPhone(
            vendorInfo.contactPersonPhoneCountryCode,
            vendorInfo.contactPersonPhoneNumber,
          ),
          branches: branchesPayload,
          ...(vendorInfo.vendorId?.trim() ? { vendor_code: vendorInfo.vendorId.trim() } : {}),
        };

        await updateVendorDraft({
          id,
          transportResourceServiceV1UpdateVendorDraftBody: updateDraftPayload,
        }).unwrap();

        const vendorLogoFile = vendorInfoForm.getValues("vendorLogoFile");
        if (vendorLogoFile) {
          const formData = new FormData();
          formData.append("file", vendorLogoFile);
          await uploadLogo({
            id,
            transportResourceServiceV1UploadVendorLogoBody: formData as TransportResourceServiceV1UploadVendorLogoBody,
          }).unwrap();
        }

        await publishVendorDraft({
          id,
          transportResourceServiceV1PublishVendorDraftBody: {
            status: vendorInfo.status || "active",
          },
        }).unwrap();
      } else {
        const updateVendorPayload: ExtendedUpdateVendorBody = {
          vendor_name: vendorInfo.vendorName,
          legal_name:
            vendorInfo.vendorType === "company" && vendorInfo.legalName
              ? vendorInfo.legalName
              : undefined,
          npwp: vendorInfo.npwp,
          vendor_type: vendorInfo.vendorType,
          status: vendorInfo.status,
          email: vendorInfo.email,
          contact_person_name: vendorInfo.contactPersonName,
          contact_person_phone_number: cleanPhone(
            vendorInfo.contactPersonPhoneCountryCode,
            vendorInfo.contactPersonPhoneNumber,
          ),
          branches: branchesPayload,
        };

        await updateVendor({
          id,
          transportResourceServiceV1UpdateVendorBody: updateVendorPayload as TransportResourceServiceV1UpdateVendorBody,
        }).unwrap();

        const vendorLogoFile = vendorInfoForm.getValues("vendorLogoFile");
        if (vendorLogoFile) {
          const formData = new FormData();
          formData.append("file", vendorLogoFile);
          await uploadLogo({
            id,
            transportResourceServiceV1UploadVendorLogoBody: formData as TransportResourceServiceV1UploadVendorLogoBody,
          }).unwrap();
        }
      }

      dispatch(vendorApi.util.invalidateTags([{ type: "Vendor", id: "LIST" }, { type: "Vendor", id: "DRAFT_LIST" }]));

      if (isDraft) {
        toast.success(
          t("toast-create-success", { name: vendorInfo.vendorName }),
        );
      } else {
        toast.success(t("toast-update-success"));
      }
      if (isDraft) {
        navigate("/master-data/vendor/draft");
      } else {
        navigate(vendorPageRouteName);
      }
    } catch (error) {
      errorHandler(error);
    }
  };

  const showSaveDraft = isDraft;

  return {
    t,
    openSections,
    toggleSection,
    vendorInfoForm,
    vendorBranchForm,
    vendorAddressForm,
    branchItems,
    addressItems,
    branchOptions,
    hasBillingAddress,
    editingBranchIndex,
    editingAddressIndex,
    isSavingVendor: isUpdatingVendor || isUpdatingVendorDraft || isPublishingVendorDraft,
    isFetchingVendor,
    showSaveDraft,
    handleNextFromInformation,
    handleNextFromBranch,
    handleAddBranch,
    handleEditBranch,
    handleDeleteBranch,
    handleAddAddress,
    handleEditAddress,
    handleDeleteAddress,
    handleConfirmAddress,
    handleSaveDraft,
    handleSave,
    showCancelModal,
    setShowCancelModal,
    navigate,
    openSection,
  };
};

export default useEditVendorDraft;
